import { ChangeDetectorRef, Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import {
	VehicleRoadWorthyCheckStatusEnum,
	VehicleRoadWorthyCheckListDto,
	VehicleRoadWorthyCheckRecordDto,
	VehicleRoadWorthyChecksAppOptionsDto,
	VehicleRoadWorthyCheckSearchDto,
	VehicleRoadWorthyChecksFilters,
	VehicleRoadWorthyChecksStatsDto,
	vehicleRoadWorthyCheckValidators,
	vehicleRoadWorthyCheckStatusToColor,
	VehicleRoadWorthyCheckRecordCategoryEnum,
	VehicleMaintenanceRecordCategoryEnum,
	CreateVehicleRoadWorthyCheckDto,
	UpdateVehicleRoadWorthyCheckDto,
	ModulesEnum,
	TagRecordCategoryEnum,
} from '@splitsecondconnect/ultramarine-shared';

import { RecordTabsComponent } from '../../../../layout/components/record-tabs/record-tabs.component';
import { TabContentDirective } from '../../../../layout/components/record-tabs/tab-content.directive';
import { BaseRecordComponent } from '../../../../shared/base-classes/base-record.component';
import { HeaderDataModel } from '../../../../shared/components/headers/record-header/record-header.model';
import { ModuleSelectorComponent } from '../../../../shared/components/module-selector/module-selector.component';
import { MoreDetailsComponent } from '../../../../shared/components/more-details/more-details.component';
import { NoActivityComponent } from '../../../../shared/components/no-activity.component';
import { AnnotationsSectionComponent } from '../../../../shared/components/annotations-section/annotations-section.component';
import { ADMINISTRATION_RECORD_TABS } from '../../../../shared/enums/administration-record-tabs';
import { dateCast } from '../../../../shared/functions/sectionCasters.function';
import {
	mapBaseDtoToBaseModel,
	mapBaseModelToBaseDto,
} from '../../../../shared/mapper/base.mapper';
import {
	mapFilesDtoToModel,
	mapFilesModelToDto,
} from '../../../../shared/mapper/files.mapper';
import {
	mapTagDtoToModel,
	mapTagModelToDto,
} from '../../../../shared/mapper/tags.mapper';
import { MODULE_SELECTOR_STRATEGY_TOKEN } from '../../../../shared/tokens/module-selector.token';
import { VehicleSectionComponent } from '../../vehicles/sections/vehicle-section/vehicle-section.component';
import { VehicleRoadWorthyChecksListItemMenuComponent } from '../components/vehicle-road-worthy-checks-list-item-menu/vehicle-road-worthy-checks-list-item-menu.component';
import { VehicleRoadWorthyCheckModuleSelectorStrategy } from '../config/vehicle-road-worthy-check-module-selector-strategy';
import { VehicleRoadWorthyCheckRecordModel } from '../models/vehicle-road-worthy-check-record.model';
import { VehicleRoadWorthyChecksService } from '../vehicle-road-worthy-checks.service';
import { VehicleRoadWorthyChecksDetailsComponent } from './components/vehicle-road-worthy-checks-details/vehicle-road-worthy-checks-details.component';
import { VehicleRoadWorthyChecksSummaryComponent } from './components/vehicle-road-worthy-checks-summary/vehicle-road-worthy-checks-summary.component';
import { TAGS_RECORD_CATEGORY_TOKEN } from '../../../../shared/tokens/tags-record-category.token';
import { CommentsSectionComponent } from 'app/shared/components/comments-section/comments-section.component';
import { StepperComponent } from '../../../../shared/components/stepper/stepper.component';
import { SectionMetadataDirective } from '../../../../shared/directives/section-metadata.directive';
import { ContextInfoComponent } from '../../../../shared/components/context-info/context-info.component';
import { ErrorsBoxComponent } from '../../../../shared/components/errors-box/errors-box.component';
import { UltraPrimaryButtonComponent } from '../../../../shared/components/ultramarine-ui/ultra-primary/ultra-primary.component';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { CommentsCardComponent } from '../../../../shared/components/comments-card/comments-card.component';
import { RecordHeaderComponent } from '../../../../shared/components/headers/record-header/record-header.component';
import { RecordFooterComponent } from '../../../../shared/components/footers/record-footer/record-footer.component';

@Component({
	selector: 'app-vehicle-road-worthy-checks-record',
	templateUrl: 'vehicle-road-worthy-checks-record.component.html',
	host: {
		'[class]': 'modalClass()',
	},
	providers: [
		{
			provide: MODULE_SELECTOR_STRATEGY_TOKEN,
			useClass: VehicleRoadWorthyCheckModuleSelectorStrategy,
		},
		{
			provide: TAGS_RECORD_CATEGORY_TOKEN,
			useValue: TagRecordCategoryEnum.VEHICLE_ROAD_WORTHY_CHECK,
		},
	],
	imports: [
		VehicleSectionComponent,
		VehicleRoadWorthyChecksSummaryComponent,
		VehicleRoadWorthyChecksDetailsComponent,
		VehicleRoadWorthyChecksListItemMenuComponent,
		AnnotationsSectionComponent,
		RecordTabsComponent,
		RecordHeaderComponent,
		RecordFooterComponent,
		AsyncPipe,
		TabContentDirective,
		IonicModule,
		ModuleSelectorComponent,
		MoreDetailsComponent,
		NoActivityComponent,
		CommentsSectionComponent,
		StepperComponent,
		SectionMetadataDirective,
		ContextInfoComponent,
		ErrorsBoxComponent,
		UltraPrimaryButtonComponent,
		NgTemplateOutlet,
		CommentsCardComponent,
	],
})
export class VehicleRoadWorthyChecksRecordComponent extends BaseRecordComponent<
	VehicleRoadWorthyCheckRecordDto,
	CreateVehicleRoadWorthyCheckDto,
	UpdateVehicleRoadWorthyCheckDto,
	VehicleRoadWorthyCheckListDto,
	VehicleRoadWorthyChecksStatsDto,
	VehicleRoadWorthyChecksAppOptionsDto,
	VehicleRoadWorthyChecksFilters,
	VehicleRoadWorthyCheckRecordModel,
	VehicleRoadWorthyCheckStatusEnum,
	VehicleRoadWorthyCheckSearchDto
> {
	vehicleMaintenanceRecordCategoryEnum = VehicleMaintenanceRecordCategoryEnum;

	constructor(
		private _vehicleRoadWorthyChecksService: VehicleRoadWorthyChecksService,
		private _activatedRoute: ActivatedRoute,
		private _router: Router,
		private _changeDetectorRef: ChangeDetectorRef,
	) {
		super(
			_vehicleRoadWorthyChecksService,
			_router,
			_activatedRoute,
			_changeDetectorRef,
		);
		this.module = ModulesEnum.VEHICLE_ROAD_WORTHY_CHECKS;
	}
	recordTabs = ADMINISTRATION_RECORD_TABS;

	recordCategories = VehicleRoadWorthyCheckRecordCategoryEnum;

	protected _isRecordFormValid(): boolean {
		const validationResult = vehicleRoadWorthyCheckValidators.canCreateOrUpdate(
			this._mapModelToDto(this.recordData()),
		);
		this.errorMessages.next(validationResult.errors);
		return validationResult.isValid;
	}

	protected _updateHeaderData(
		record: VehicleRoadWorthyCheckRecordModel,
	): HeaderDataModel<VehicleRoadWorthyCheckStatusEnum> {
		return {
			...this._updateHeaderDataBase(record),
			title: record.code,
			status: record.status,

			color: vehicleRoadWorthyCheckStatusToColor(record.status),
		};
	}

	protected _mapDtoToModel(
		dto: VehicleRoadWorthyCheckRecordDto,
	): VehicleRoadWorthyCheckRecordModel {
		return {
			...mapBaseDtoToBaseModel(dto),
			code: dto.code ?? null,
			notes: dto.notes ?? null,
			externalReference: dto.externalReference ?? null,
			startsAt: dateCast(dto.startsAt),
			endsAt: dateCast(dto.endsAt),
			status: dto.status ?? null,
			vehicleId: dto.vehicleId ?? null,

			taxRateType: dto.taxRateType ?? null,
			taxRate: dto.taxRate ?? null,
			isTaxIncluded: dto.isTaxIncluded ?? null,
			amount: dto.amount ?? null,
			totalNet: dto.totalNet ?? null,
			totalTax: dto.totalTax ?? null,
			totalGross: dto.totalGross ?? null,
			recordCategory: dto.recordCategory ?? null,
			...mapTagDtoToModel(dto.tags),
			...mapFilesDtoToModel(dto.files),
		};
	}

	protected _mapModelToDto(
		model: VehicleRoadWorthyCheckRecordModel,
	): VehicleRoadWorthyCheckRecordDto {
		return {
			...mapBaseModelToBaseDto(model),
			code: model.code ?? null,
			notes: model.notes ?? null,
			externalReference: model.externalReference ?? null,
			startsAt: dateCast(model.startsAt),
			endsAt: dateCast(model.endsAt),
			vehicleId: model.vehicleId ?? null,

			taxRateType: model.taxRateType ?? null,
			taxRate: model.taxRate ?? null,
			isTaxIncluded: model.isTaxIncluded ?? null,
			amount: model.amount ?? null,
			totalNet: model.totalNet ?? null,
			totalTax: model.totalTax ?? null,
			totalGross: model.totalGross ?? null,
			recordCategory: model.recordCategory ?? null,
			...mapTagModelToDto(model.tags),
			...mapFilesModelToDto(model.files),
			status: model.status ?? null,
		};
	}

	protected _mapModelToCreateDto(
		model: VehicleRoadWorthyCheckRecordModel,
	): CreateVehicleRoadWorthyCheckDto {
		return {
			...mapBaseModelToBaseDto(model),
			code: model.code ?? null,
			notes: model.notes ?? null,
			externalReference: model.externalReference ?? null,
			startsAt: dateCast(model.startsAt),
			endsAt: dateCast(model.endsAt),
			vehicleId: model.vehicleId ?? null,

			taxRateType: model.taxRateType ?? null,
			taxRate: model.taxRate ?? null,
			isTaxIncluded: model.isTaxIncluded ?? null,
			amount: model.amount ?? null,
			totalNet: model.totalNet ?? null,
			totalTax: model.totalTax ?? null,
			totalGross: model.totalGross ?? null,
			recordCategory: model.recordCategory ?? null,
			...mapTagModelToDto(model.tags),
			...mapFilesModelToDto(model.files),
		};
	}
	protected _mapModelToUpdateDto(
		model: VehicleRoadWorthyCheckRecordModel,
	): UpdateVehicleRoadWorthyCheckDto {
		return {
			...mapBaseModelToBaseDto(model),
			code: model.code ?? null,
			notes: model.notes ?? null,
			externalReference: model.externalReference ?? null,
			startsAt: dateCast(model.startsAt),
			endsAt: dateCast(model.endsAt),
			vehicleId: model.vehicleId ?? null,

			taxRateType: model.taxRateType ?? null,
			taxRate: model.taxRate ?? null,
			isTaxIncluded: model.isTaxIncluded ?? null,
			amount: model.amount ?? null,
			totalNet: model.totalNet ?? null,
			totalTax: model.totalTax ?? null,
			totalGross: model.totalGross ?? null,
			recordCategory: model.recordCategory ?? null,
			...mapTagModelToDto(model.tags),
			...mapFilesModelToDto(model.files),
		};
	}
}
